import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useWebSocket, useWsEvent } from '@/hooks/use-websocket';
import { useDashboardOperationalSignals } from '@/hooks/use-dashboard-operational-signals';
import { useDashboardStreamEvents } from '@/hooks/use-dashboard-stream-events';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, FileSearch, Utensils, UsersRound, Percent, ShoppingBag, X, CalendarX } from 'lucide-react';
import { listBookings, listRooms, updateRoomStatus as apiUpdateRoomStatus, getGlobalTaxRate, updateGlobalTaxRate, getClosedDates, updateClosedDates, createQuickSale, } from '@/services/pos-api';
import { formatClosedDate } from '@/components/closure-banner';
import { BookingModal } from './booking-modal';
import { AdminHeader } from '@/components/AdminHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { WsStatusDot } from '@/components/WsStatusDot';
import ClockModal from './clock-modal';
import ManagerPanel from './manager-panel';
import { MCDataStream, MCStatDot, MCRoomRail, MCTelemetryRail, MCToolsRail, MCActionDock, MCHealthDot, MCAttentionBell, MCAttentionList, MCLogTail, MCLogDetailDialog, MCTaxDialog, MCClosedDatesDialog, TimelineView, } from '@/components/mc';
import { VENUE_TIMEZONE, todayRange, weekRange, toDateStringInTz, getTimePartsInTz, } from '@/lib/timezone';
export default function POSDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isReadOnly = user?.role === 'SALES';
    const isStaff = user?.role === 'STAFF';
    const isAdmin = user?.role === 'ADMIN';
    const canUseManagerConsole = isAdmin || isStaff;
    const canViewReceiptOps = isAdmin || isStaff;
    const [selectedLog, setSelectedLog] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [taxRate, setTaxRate] = useState(8);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        return new Date();
    });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [preselectedRoomId, setPreselectedRoomId] = useState(undefined);
    const [showClockModal, setShowClockModal] = useState(false);
    const [showManagerConsole, setShowManagerConsole] = useState(false);
    const [lastSync, setLastSync] = useState(undefined);
    const [pendingRoomStatus, setPendingRoomStatus] = useState(null);
    const [showTaxDialog, setShowTaxDialog] = useState(false);
    const [showClosuresDialog, setShowClosuresDialog] = useState(false);
    const [closedDates, setClosedDates] = useState([]);
    const [timelineTz, setTimelineTz] = useState(() => {
        return localStorage.getItem('pos-timeline-tz') || 'venue';
    });
    const activeTimezone = timelineTz === 'venue'
        ? VENUE_TIMEZONE
        : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const selectedDashboardDate = useMemo(() => toDateStringInTz(currentWeekStart, activeTimezone), [currentWeekStart, activeTimezone]);
    const operationalSignals = useDashboardOperationalSignals({
        selectedDate: selectedDashboardDate,
        canViewReceiptOps,
        isAdmin,
        bookings,
        currentTime,
    });
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        if (!showManagerConsole)
            return;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape')
                setShowManagerConsole(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showManagerConsole]);
    useEffect(() => {
        loadData(true);
        loadTaxRate();
        loadClosedDates();
    }, []);
    useEffect(() => {
        loadData(false);
    }, [currentWeekStart, activeTimezone]);
    // Realtime: WS drives updates normally; polling kicks in only when WS down >60s.
    const { isPollingFallback } = useWebSocket();
    // Polling fallback: only runs while WS is down >60s. When WS is healthy, the
    // WS subscriptions below handle all updates without any timer.
    useEffect(() => {
        if (!isPollingFallback)
            return;
        const pollInterval = setInterval(async () => {
            try {
                const today = todayRange();
                const week = weekRange(currentWeekStart);
                const [todayBookingsData, weekBookingsData, roomsData] = await Promise.all([
                    listBookings({ startDate: today.start, endDate: today.end, limit: 100 }),
                    listBookings({ startDate: week.start, endDate: week.end, limit: 500 }),
                    listRooms(),
                ]);
                const bookingsMap = new Map();
                weekBookingsData.forEach((b) => bookingsMap.set(b.id, b));
                todayBookingsData.forEach((b) => bookingsMap.set(b.id, b));
                const mergedBookings = Array.from(bookingsMap.values()).map((b) => {
                    const start = new Date(b.startTime);
                    const end = new Date(b.endTime);
                    const room = roomsData.find((r) => r.id === b.roomId);
                    const isQuickSale = b.bookingSource === 'QUICK_SALE';
                    const localDate = toDateStringInTz(start, activeTimezone);
                    const tp = getTimePartsInTz(start, activeTimezone);
                    const localTime = `${String(tp.hours).padStart(2, '0')}:${String(tp.minutes).padStart(2, '0')}`;
                    return {
                        ...b,
                        date: localDate,
                        time: localTime,
                        duration: (end.getTime() - start.getTime()) / (1000 * 60 * 60),
                        roomName: isQuickSale ? 'Quick Sale' : room?.name || 'Unknown Room',
                    };
                });
                setBookings(mergedBookings);
                setRooms(roomsData);
                setLastSync(new Date());
            }
            catch (err) {
                console.debug('[Dashboard] Poll update skipped:', err);
            }
        }, 5000);
        return () => clearInterval(pollInterval);
    }, [currentWeekStart, isPollingFallback, activeTimezone]);
    // WebSocket subscription: targeted refetch on any staff-facing mutation.
    // When WS is healthy, polling is disabled (see poll effect above). When WS is
    // disconnected for >60s, the polling fallback kicks in automatically.
    const refetchFromEvent = React.useCallback(() => { loadData(false); }, [currentWeekStart, activeTimezone]);
    useWsEvent('booking.status_changed', refetchFromEvent);
    useWsEvent('booking.created', refetchFromEvent);
    useWsEvent('booking.updated', refetchFromEvent);
    useWsEvent('booking.cancelled', refetchFromEvent);
    useWsEvent('booking.completed', refetchFromEvent);
    useWsEvent('payment.status_changed', refetchFromEvent);
    useWsEvent('invoice.paid', refetchFromEvent);
    useWsEvent('invoice.unpaid', refetchFromEvent);
    useWsEvent('invoice.payment_added', refetchFromEvent);
    useWsEvent('order.created', refetchFromEvent);
    useWsEvent('order.updated', refetchFromEvent);
    useWsEvent('order.deleted', refetchFromEvent);
    useWsEvent('room.updated', refetchFromEvent);
    async function loadData(showLoading = true) {
        try {
            if (showLoading)
                setLoading(true);
            const today = todayRange();
            const week = weekRange(currentWeekStart);
            const [todayBookingsData, weekBookingsData, roomsData] = await Promise.all([
                listBookings({ startDate: today.start, endDate: today.end, limit: 100 }),
                listBookings({ startDate: week.start, endDate: week.end, limit: 500 }),
                listRooms(),
            ]);
            const bookingsMap = new Map();
            weekBookingsData.forEach((b) => bookingsMap.set(b.id, b));
            todayBookingsData.forEach((b) => bookingsMap.set(b.id, b));
            const transformedBookings = Array.from(bookingsMap.values()).map((b) => {
                const start = new Date(b.startTime);
                const end = new Date(b.endTime);
                const room = roomsData.find((r) => r.id === b.roomId);
                const isQuickSale = b.bookingSource === 'QUICK_SALE';
                const localDate = toDateStringInTz(start, activeTimezone);
                const tp = getTimePartsInTz(start, activeTimezone);
                const localTime = `${String(tp.hours).padStart(2, '0')}:${String(tp.minutes).padStart(2, '0')}`;
                return {
                    ...b,
                    date: localDate,
                    time: localTime,
                    duration: (end.getTime() - start.getTime()) / (1000 * 60 * 60),
                    roomName: isQuickSale ? 'Quick Sale' : room?.name || 'Unknown Room',
                };
            });
            setBookings(transformedBookings);
            setRooms(roomsData);
            setLastSync(new Date());
        }
        catch (err) {
            console.error('[POS Dashboard] Failed to load data:', err);
            if (showLoading) {
                alert(`Failed to load data: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
        }
        finally {
            if (showLoading)
                setLoading(false);
        }
    }
    async function loadTaxRate() {
        try {
            const rate = await getGlobalTaxRate();
            setTaxRate(rate);
        }
        catch (err) {
            console.error('Failed to load tax rate:', err);
        }
    }
    async function loadClosedDates() {
        try {
            const dates = await getClosedDates();
            setClosedDates(dates);
        }
        catch (err) {
            console.error('Failed to load closed dates:', err);
        }
    }
    async function updateRoomStatus(id, status) {
        try {
            await apiUpdateRoomStatus(id, status);
            await loadData();
        }
        catch (err) {
            console.error('Failed to update room:', err);
        }
    }
    function openBookingDetail(bookingId) {
        navigate(`/pos/booking/${bookingId}`);
    }
    const currentBookings = useMemo(() => {
        const now = currentTime;
        return bookings.filter((b) => {
            const start = new Date(b.startTime);
            const end = new Date(b.endTime);
            const status = (b.bookingStatus || b.status || '').toUpperCase();
            return b.bookingSource !== 'QUICK_SALE' && now >= start && now <= end && status !== 'CANCELLED' && status !== 'COMPLETED';
        });
    }, [bookings, currentTime]);
    const selectedDayQuickSales = useMemo(() => {
        return bookings
            .filter((b) => {
            const status = (b.bookingStatus || b.status || '').toUpperCase();
            return b.bookingSource === 'QUICK_SALE' && b.date === selectedDashboardDate && status !== 'CANCELLED' && status !== 'COMPLETED';
        })
            .sort((a, b) => new Date(b.createdAt || b.startTime).getTime() - new Date(a.createdAt || a.startTime).getTime());
    }, [bookings, selectedDashboardDate]);
    // Derive the real-time data stream from bookings/rooms
    const derivedStreamEvents = useDashboardStreamEvents(bookings, currentTime);
    const streamEvents = useMemo(() => [...operationalSignals.streamEvents, ...derivedStreamEvents]
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 50), [operationalSignals.streamEvents, derivedStreamEvents]);
    const logLines = useMemo(() => streamEvents
        .slice()
        .reverse()
        .map((event) => {
        const level = event.level ?? (event.type === 'OcrHealth' || event.type === 'ReceiptReview' ? 'WARN' : 'INFO');
        return {
            id: `ops-${event.id}`,
            ts: event.timestamp,
            level,
            source: event.type,
            message: `${event.type}: ${event.secondary ? `${event.secondary} · ` : ''}${event.primary}`,
            payload: {
                type: event.type,
                primary: event.primary,
                secondary: event.secondary,
                meta: event.meta,
                linkHref: event.linkHref,
            },
        };
    }), [streamEvents]);
    const activeCount = currentBookings.length;
    if (loading) {
        return (_jsx("div", { className: "mc-root flex items-center justify-center", children: _jsxs("div", { className: "flex flex-col items-center gap-4", children: [_jsx(MCStatDot, { variant: "cyan", pulse: true }), _jsx("p", { className: "mc-meta mc-mono", children: "initializing\u2026" })] }) }));
    }
    const toolsRailItems = [
        {
            id: 'clock',
            label: 'Clock',
            icon: _jsx(Clock, { className: "h-4 w-4" }),
            onClick: () => setShowClockModal(true),
        },
        {
            id: 'menu',
            label: 'Menu',
            icon: _jsx(Utensils, { className: "h-4 w-4" }),
            to: '/pos/menu',
            hidden: isReadOnly,
        },
        {
            id: 'time-mgmt',
            label: 'Time Mgmt',
            icon: _jsx(Users, { className: "h-4 w-4" }),
            to: '/pos/time-management',
            hidden: user?.role !== 'ADMIN',
        },
        {
            id: 'receipt-analysis',
            label: 'OCR Analysis',
            icon: _jsx(FileSearch, { className: "h-4 w-4" }),
            to: '/admin/receipt-analysis',
            badge: operationalSignals.receiptReviewCount,
            alert: operationalSignals.receiptReviewCount > 0,
            hidden: !canViewReceiptOps,
        },
        {
            id: 'customers',
            label: 'Customers',
            icon: _jsx(UsersRound, { className: "h-4 w-4" }),
            to: '/admin/customers',
            hidden: user?.role !== 'ADMIN' && user?.role !== 'SALES',
        },
        {
            id: 'tax',
            label: 'Tax',
            icon: _jsx(Percent, { className: "h-4 w-4" }),
            onClick: () => setShowTaxDialog(true),
            hidden: isReadOnly,
        },
        {
            id: 'closures',
            label: 'Closures',
            icon: _jsx(CalendarX, { className: "h-4 w-4" }),
            onClick: () => setShowClosuresDialog(true),
            hidden: user?.role !== 'ADMIN',
        },
    ];
    const venueTodayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Halifax' });
    const upcomingClosures = closedDates
        .filter((c) => c.date >= venueTodayStr)
        .sort((a, b) => a.date.localeCompare(b.date));
    return (_jsxs("div", { className: "mc-root lg:h-screen lg:overflow-hidden", children: [_jsx(AdminHeader, { title: "K one Golf", subtitle: "// POS \u00B7 MISSION CONTROL", variant: "mc", navItems: [
                    {
                        label: 'Manager',
                        onClick: () => setShowManagerConsole(true),
                        show: canUseManagerConsole,
                    },
                ], mcRightExtras: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(WsStatusDot, {}), _jsx(MCHealthDot, { enabled: isAdmin, health: operationalSignals.ocrHealth, lastProbe: operationalSignals.lastOcrProbe }), _jsx("span", { "aria-hidden": true, className: "inline-block h-5 w-px", style: { background: 'var(--mc-divider)' } }), _jsx(MCAttentionBell, { items: operationalSignals.attentionItems, readIds: operationalSignals.attentionReadIds, onMarkRead: operationalSignals.markAttentionRead, onOpenItem: (item) => {
                                if (item.linkHref)
                                    navigate(item.linkHref);
                            } })] }) }), _jsx(MCRoomRail, { rooms: rooms, bookings: bookings, isReadOnly: isReadOnly, onChangeStatus: (roomId, nextStatus) => {
                    const room = rooms.find((r) => r.id === roomId);
                    if (!room || room.status === nextStatus)
                        return;
                    setPendingRoomStatus({
                        roomId: room.id,
                        roomName: room.name,
                        nextStatus,
                    });
                }, onSelectRoom: (roomId) => {
                    const current = currentBookings.find((b) => b.roomId === roomId);
                    if (current) {
                        openBookingDetail(current.id);
                    }
                    else if (!isReadOnly) {
                        setPreselectedRoomId(roomId);
                        setShowCreateModal(true);
                    }
                } }), _jsx(MCTelemetryRail, { bookingsCount: bookings.length, roomsCount: rooms.length, activeCount: activeCount, lastSync: lastSync }), _jsx("main", { className: "mc-one-screen mx-auto px-4 sm:px-8 2xl:pl-[224px] 2xl:pr-[244px] 2xl:px-0 py-4 lg:py-3 max-w-[1800px] 2xl:max-w-none lg:h-[calc(100dvh-56px)] lg:overflow-hidden", children: _jsxs("div", { className: "grid grid-cols-1 gap-2 lg:h-full lg:grid-rows-[auto_minmax(0,1fr)] min-h-0", children: [_jsxs("div", { className: "flex flex-col gap-2 min-w-0 lg:min-h-0", children: [upcomingClosures.length > 0 && (_jsxs("div", { className: "mc-panel flex items-center gap-3 px-4 py-2 flex-wrap", style: { borderLeft: '3px solid var(--mc-amber, #f59e0b)' }, role: "status", "aria-label": "Upcoming venue closures", children: [_jsx(CalendarX, { className: "h-4 w-4 flex-shrink-0", style: { color: 'var(--mc-amber, #f59e0b)' } }), _jsx("span", { className: "mc-section-label", style: { color: 'var(--mc-amber, #f59e0b)' }, children: "Closed" }), _jsx("span", { className: "flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[color:var(--mc-text-primary)] min-w-0", children: upcomingClosures.map((c) => (_jsxs("span", { className: "whitespace-nowrap", children: [formatClosedDate(c.date), c.reason ? (_jsxs("span", { className: "text-[color:var(--mc-text-meta,#94a3b8)]", children: [" \u2014 ", c.reason] })) : null] }, c.date))) }), isAdmin && (_jsx("button", { type: "button", className: "mc-btn ml-auto", onClick: () => setShowClosuresDialog(true), children: "Manage" }))] })), _jsx(TimelineView, { bookings: bookings, rooms: rooms, onBookingClick: openBookingDetail, currentWeekStart: currentWeekStart, setCurrentWeekStart: setCurrentWeekStart, taxRate: taxRate, activeTimezone: activeTimezone, timelineTz: timelineTz, setTimelineTz: setTimelineTz, daysToShow: 1, navStep: "day", compact: true, closedDates: closedDates })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[250px_minmax(250px,0.85fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-2 lg:min-h-0 lg:h-full", children: [_jsxs("div", { className: "mc-panel py-3 flex flex-col min-h-[260px] lg:min-h-0 overflow-y-auto mc-scroll-thin", children: [!isReadOnly && (_jsxs(_Fragment, { children: [_jsx("div", { className: "mc-section-label px-4 pb-2", children: "Actions" }), _jsx("div", { className: "px-4 pb-3", children: _jsx(MCActionDock, { onCreateBooking: () => setShowCreateModal(true), onQuickSale: async () => {
                                                            try {
                                                                const booking = await createQuickSale();
                                                                navigate(`/pos/booking/${booking.id}`);
                                                            }
                                                            catch (err) {
                                                                alert(err.message || 'Failed to create quick sale');
                                                            }
                                                        } }) }), _jsx("div", { "aria-hidden": true, className: "mx-4", style: { height: 1, background: 'var(--mc-divider-soft)' } })] })), _jsx("div", { className: "mc-section-label px-4 pt-3 pb-2", children: "Tools" }), _jsx(MCToolsRail, { items: toolsRailItems, variant: "vertical" })] }), _jsxs("div", { className: "grid grid-rows-[minmax(0,2fr)_minmax(0,3fr)] gap-2 min-h-[520px] lg:min-h-0 lg:h-full overflow-hidden", children: [_jsxs("section", { className: "mc-panel py-0 flex flex-col min-h-0 overflow-hidden", style: { padding: 0 }, "aria-labelledby": "dashboard-quick-sales-heading", children: [_jsxs("div", { className: "shrink-0 h-8 flex items-center justify-between gap-2 px-4", style: { borderBottom: '1px solid var(--mc-divider-soft)' }, children: [_jsx("h2", { id: "dashboard-quick-sales-heading", className: "mc-section-label", children: "Quick Sales" }), selectedDayQuickSales.length > 0 ? (_jsxs("span", { className: "mc-mono text-[11px] text-[color:var(--mc-text-accent-pink)]", children: [selectedDayQuickSales.length, " open"] })) : null] }), selectedDayQuickSales.length === 0 ? (_jsx("div", { className: "flex-1 px-4 py-2 mc-meta-dim mc-mono", children: "// no open quick sales" })) : (_jsx("div", { className: "flex-1 min-h-0 overflow-y-auto mc-scroll-thin", children: selectedDayQuickSales.map((sale, idx) => (_jsxs("button", { type: "button", onClick: () => openBookingDetail(sale.id), className: "w-full h-7 text-left px-4 flex items-center gap-2.5 transition-colors hover:bg-[color:var(--mc-surface-raised)] focus:outline-none focus-visible:bg-[color:var(--mc-surface-raised)]", style: { borderTop: idx === 0 ? 'none' : '1px solid var(--mc-divider-soft)' }, "aria-label": `Open quick sale ${sale.id}`, children: [_jsx(ShoppingBag, { className: "h-3.5 w-3.5 text-[color:var(--mc-magenta)] shrink-0" }), _jsxs("span", { className: "min-w-0 flex-1 flex items-center gap-2", children: [_jsx("span", { className: "text-xs font-semibold text-[color:var(--mc-text-primary)] truncate", children: sale.customerName || 'Quick Sale' }), _jsx("span", { className: "mc-meta-dim mc-mono shrink-0", children: sale.time })] }), _jsxs("span", { className: "mc-mono text-xs text-[color:var(--mc-text-accent-pink)]", children: ["$", sale.price.toFixed(2)] })] }, sale.id))) }))] }), _jsx("div", { className: "mc-panel py-0 flex flex-col min-h-0 overflow-hidden", style: { padding: 0 }, "aria-label": "Attention panel", children: _jsx(MCAttentionList, { items: operationalSignals.attentionItems, readIds: operationalSignals.attentionReadIds, onMarkRead: operationalSignals.markAttentionRead, onOpenItem: (item) => {
                                                    if (item.linkHref)
                                                        navigate(item.linkHref);
                                                }, listClassName: "flex-1 min-h-0 overflow-y-auto" }) })] }), _jsx("div", { className: "mc-panel py-4 min-h-[300px] lg:min-h-0 overflow-hidden", children: _jsx(MCDataStream, { events: streamEvents, onOpenEvent: (event) => {
                                            if (event.linkHref)
                                                navigate(event.linkHref);
                                        } }) }), _jsx("div", { className: "mc-panel py-3 px-4 min-h-[300px] lg:min-h-0 overflow-hidden", children: _jsx(MCLogTail, { lines: logLines, heading: "// OPS \u00B7 EVENT STREAM", onLineClick: setSelectedLog }) })] })] }) }), showManagerConsole && canUseManagerConsole && (_jsx("div", { className: "fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-3 sm:p-5", role: "dialog", "aria-modal": "true", "aria-label": "Manager Console", children: _jsxs("div", { className: "mc-panel h-full max-w-[1180px] ml-auto p-0 flex flex-col shadow-2xl", children: [_jsxs("div", { className: "mc-dialog-header", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("div", { className: "mc-section-label", children: "Manager Console" }), _jsx("div", { className: "mc-meta mt-1", children: "Customers, bookings, and manager-only controls" })] }), _jsxs("button", { type: "button", className: "mc-chip ml-auto", onClick: () => setShowManagerConsole(false), "aria-label": "Close Manager Console", children: [_jsx(X, { className: "h-3.5 w-3.5" }), "Close"] })] }), _jsx("div", { className: "flex-1 min-h-0 overflow-y-auto mc-scroll-thin p-4", children: _jsx(ManagerPanel, {}) })] }) })), _jsx(BookingModal, { isOpen: showCreateModal, onClose: () => {
                    setShowCreateModal(false);
                    setPreselectedRoomId(undefined);
                }, rooms: rooms, onSuccess: (booking) => {
                    loadData();
                    navigate(`/pos/booking/${booking.id}`);
                }, preselectedRoomId: preselectedRoomId, variant: "mission-control" }), _jsx(ConfirmDialog, { open: pendingRoomStatus !== null, onOpenChange: (o) => !o && setPendingRoomStatus(null), title: `Change ${pendingRoomStatus?.roomName ?? ''} to ${pendingRoomStatus?.nextStatus ?? ''}?`, description: pendingRoomStatus?.nextStatus === 'CLOSED'
                    ? 'This room will no longer accept new bookings until reopened.'
                    : pendingRoomStatus?.nextStatus === 'MAINTENANCE'
                        ? 'This room will be marked as under maintenance and blocked from new bookings.'
                        : 'This room will become available for new bookings.', confirmLabel: "Change Status", destructive: pendingRoomStatus?.nextStatus !== 'ACTIVE', onConfirm: () => {
                    if (pendingRoomStatus) {
                        updateRoomStatus(pendingRoomStatus.roomId, pendingRoomStatus.nextStatus);
                    }
                    setPendingRoomStatus(null);
                } }), _jsx(ClockModal, { isOpen: showClockModal, onClose: () => setShowClockModal(false) }), _jsx(MCTaxDialog, { open: showTaxDialog, onOpenChange: setShowTaxDialog, currentRate: taxRate, onSave: async (rate) => {
                    await updateGlobalTaxRate(rate);
                    setTaxRate(rate);
                } }), _jsx(MCClosedDatesDialog, { open: showClosuresDialog, onOpenChange: setShowClosuresDialog, currentDates: closedDates, onSave: async (dates) => {
                    await updateClosedDates(dates);
                    setClosedDates(dates);
                } }), _jsx(MCLogDetailDialog, { line: selectedLog, onOpenChange: (open) => {
                    if (!open)
                        setSelectedLog(null);
                } })] }));
}
